import React, { useState, useEffect } from 'react';
import { Plus, Trash2, ArrowUp, ArrowDown, Save, X, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { toast } from 'sonner';
import type { ApprovalWorkflow, WorkflowStage, WorkflowApprover, ApprovalType, ApproverType, CreateWorkflowRequest, UpdateWorkflowRequest } from '@/types/workflow';
import type { Employee } from '@/types/timesheet';
import { createWorkflow, updateWorkflow } from '@/services/workflowService';
import { AutomationRuleBuilder } from '@/components/AutomationRuleBuilder';

interface WorkflowBuilderProps {
  workflow?: ApprovalWorkflow;
  employees: Employee[];
  onSave: () => void;
  onCancel: () => void;
}

export function WorkflowBuilder({ workflow, employees, onSave, onCancel }: WorkflowBuilderProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [isDefault, setIsDefault] = useState(false);
  const [stages, setStages] = useState<WorkflowStage[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [editingRulesForStage, setEditingRulesForStage] = useState<number | null>(null);

  useEffect(() => {
    if (workflow) {
      setName(workflow.name);
      setDescription(workflow.description || '');
      setIsActive(workflow.isActive);
      setIsDefault(workflow.isDefault);
      setStages(workflow.stages);
    } else {
      // Initialize with one empty stage
      addStage();
    }
  }, [workflow]);

  const addStage = () => {
    const newStage: WorkflowStage = {
      name: `Stage ${stages.length + 1}`,
      description: '',
      stageOrder: stages.length + 1,
      approvalType: 'any',
      requiredApprovals: 1,
      approvers: [],
    };
    setStages([...stages, newStage]);
  };

  const removeStage = (index: number) => {
    const newStages = stages.filter((_, i) => i !== index);
    // Reorder remaining stages
    newStages.forEach((stage, i) => {
      stage.stageOrder = i + 1;
    });
    setStages(newStages);
  };

  const moveStage = (index: number, direction: 'up' | 'down') => {
    const newStages = [...stages];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex < 0 || targetIndex >= newStages.length) return;
    
    [newStages[index], newStages[targetIndex]] = [newStages[targetIndex], newStages[index]];
    
    // Update stage orders
    newStages.forEach((stage, i) => {
      stage.stageOrder = i + 1;
    });
    
    setStages(newStages);
  };

  const updateStage = (index: number, updates: Partial<WorkflowStage>) => {
    const newStages = [...stages];
    newStages[index] = { ...newStages[index], ...updates };
    setStages(newStages);
  };

  const addApprover = (stageIndex: number) => {
    const newApprover: WorkflowApprover = {
      approverType: 'specific',
      approverOrder: stages[stageIndex].approvers.length,
    };
    
    const newStages = [...stages];
    newStages[stageIndex].approvers.push(newApprover);
    setStages(newStages);
  };

  const removeApprover = (stageIndex: number, approverIndex: number) => {
    const newStages = [...stages];
    newStages[stageIndex].approvers = newStages[stageIndex].approvers.filter(
      (_, i) => i !== approverIndex
    );
    // Reorder remaining approvers
    newStages[stageIndex].approvers.forEach((approver, i) => {
      approver.approverOrder = i;
    });
    setStages(newStages);
  };

  const updateApprover = (
    stageIndex: number,
    approverIndex: number,
    updates: Partial<WorkflowApprover>
  ) => {
    const newStages = [...stages];
    newStages[stageIndex].approvers[approverIndex] = {
      ...newStages[stageIndex].approvers[approverIndex],
      ...updates,
    };
    setStages(newStages);
  };

  const validate = (): string[] => {
    const errors: string[] = [];

    if (!name.trim()) {
      errors.push('Workflow name is required');
    }

    if (stages.length === 0) {
      errors.push('At least one stage is required');
    }

    stages.forEach((stage, index) => {
      if (!stage.name.trim()) {
        errors.push(`Stage ${index + 1}: Name is required`);
      }

      if (stage.approvers.length === 0) {
        errors.push(`Stage ${index + 1}: At least one approver is required`);
      }

      stage.approvers.forEach((approver, approverIndex) => {
        if (approver.approverType === 'specific' && !approver.approverId) {
          errors.push(
            `Stage ${index + 1}, Approver ${approverIndex + 1}: Employee selection is required`
          );
        }
        if (approver.approverType === 'role' && !approver.approverRole) {
          errors.push(
            `Stage ${index + 1}, Approver ${approverIndex + 1}: Role is required`
          );
        }
      });
    });

    return errors;
  };

  const handleSave = async () => {
    const errors = validate();
    if (errors.length > 0) {
      errors.forEach((error) => toast.error(error));
      return;
    }

    setIsSaving(true);
    try {
      if (workflow) {
        // Update existing workflow
        const request: UpdateWorkflowRequest = {
          name,
          description,
          isActive,
          isDefault,
          stages: stages.map((stage) => ({
            id: stage.id,
            name: stage.name,
            description: stage.description,
            stageOrder: stage.stageOrder,
            approvalType: stage.approvalType,
            requiredApprovals: stage.requiredApprovals,
            approvers: stage.approvers.map((approver) => ({
              id: approver.id,
              approverType: approver.approverType,
              approverId: approver.approverId,
              approverRole: approver.approverRole,
              approverOrder: approver.approverOrder,
            })),
          })),
        };
        await updateWorkflow(workflow.id, request);
        toast.success('Workflow updated successfully');
      } else {
        // Create new workflow
        const request: CreateWorkflowRequest = {
          name,
          description,
          isActive,
          isDefault,
          stages: stages.map((stage) => ({
            name: stage.name,
            description: stage.description,
            stageOrder: stage.stageOrder,
            approvalType: stage.approvalType,
            requiredApprovals: stage.requiredApprovals,
            approvers: stage.approvers.map((approver) => ({
              approverType: approver.approverType,
              approverId: approver.approverId,
              approverRole: approver.approverRole,
              approverOrder: approver.approverOrder,
            })),
          })),
        };
        await createWorkflow(request);
        toast.success('Workflow created successfully');
      }
      onSave();
    } catch (error: any) {
      toast.error(error.message || 'Failed to save workflow');
      console.error('Save workflow error:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">
            {workflow ? 'Edit Workflow' : 'Create New Workflow'}
          </h2>
          <p className="text-muted-foreground">
            {workflow ? 'Modify the workflow configuration' : 'Configure a new approval workflow'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={onCancel} disabled={isSaving}>
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            <Save className="h-4 w-4 mr-2" />
            {isSaving ? 'Saving...' : 'Save Workflow'}
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Workflow Details</CardTitle>
          <CardDescription>Basic information about the workflow</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Name *</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Standard Timesheet Approval"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe the purpose of this workflow..."
              rows={3}
            />
          </div>

          <div className="flex items-center gap-6">
            <div className="flex items-center space-x-2">
              <Switch id="isActive" checked={isActive} onCheckedChange={setIsActive} />
              <Label htmlFor="isActive">Active</Label>
            </div>

            <div className="flex items-center space-x-2">
              <Switch id="isDefault" checked={isDefault} onCheckedChange={setIsDefault} />
              <Label htmlFor="isDefault">Set as Default</Label>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold">Approval Stages</h3>
            <p className="text-sm text-muted-foreground">
              Define the stages and approvers for this workflow
            </p>
          </div>
          <Button onClick={addStage} variant="outline">
            <Plus className="h-4 w-4 mr-2" />
            Add Stage
          </Button>
        </div>

        {stages.map((stage, stageIndex) => (
          <Card key={stageIndex}>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge>Stage {stage.stageOrder}</Badge>
                    <Input
                      value={stage.name}
                      onChange={(e) => updateStage(stageIndex, { name: e.target.value })}
                      placeholder="Stage name"
                      className="max-w-xs"
                    />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => moveStage(stageIndex, 'up')}
                    disabled={stageIndex === 0}
                  >
                    <ArrowUp className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => moveStage(stageIndex, 'down')}
                    disabled={stageIndex === stages.length - 1}
                  >
                    <ArrowDown className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeStage(stageIndex)}
                    disabled={stages.length === 1}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={stage.description || ''}
                  onChange={(e) => updateStage(stageIndex, { description: e.target.value })}
                  placeholder="Describe this stage..."
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Approval Type</Label>
                  <Select
                    value={stage.approvalType}
                    onValueChange={(value: ApprovalType) =>
                      updateStage(stageIndex, { approvalType: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="any">Any (one approver)</SelectItem>
                      <SelectItem value="all">All (all approvers)</SelectItem>
                      <SelectItem value="majority">Majority</SelectItem>
                      <SelectItem value="sequential">Sequential</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Required Approvals</Label>
                  <Input
                    type="number"
                    min="1"
                    value={stage.requiredApprovals}
                    onChange={(e) =>
                      updateStage(stageIndex, {
                        requiredApprovals: parseInt(e.target.value) || 1,
                      })
                    }
                  />
                </div>
              </div>

              <Separator />

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label>Approvers</Label>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => addApprover(stageIndex)}
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Add Approver
                  </Button>
                </div>

                {stage.approvers.map((approver, approverIndex) => (
                  <div key={approverIndex} className="flex items-end gap-2 p-3 border rounded-lg">
                    <div className="flex-1 grid grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <Label className="text-xs">Type</Label>
                        <Select
                          value={approver.approverType}
                          onValueChange={(value: ApproverType) =>
                            updateApprover(stageIndex, approverIndex, {
                              approverType: value,
                              approverId: undefined,
                              approverRole: undefined,
                            })
                          }
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="specific">Specific Employee</SelectItem>
                            <SelectItem value="role">Role</SelectItem>
                            <SelectItem value="manager">Direct Manager</SelectItem>
                            <SelectItem value="department_head">Department Head</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {approver.approverType === 'specific' && (
                        <div className="space-y-1">
                          <Label className="text-xs">Employee</Label>
                          <Select
                            value={approver.approverId || ''}
                            onValueChange={(value) =>
                              updateApprover(stageIndex, approverIndex, { approverId: value })
                            }
                          >
                            <SelectTrigger className="h-9">
                              <SelectValue placeholder="Select employee" />
                            </SelectTrigger>
                            <SelectContent>
                              {employees.map((emp) => (
                                <SelectItem key={emp.id} value={emp.id}>
                                  {emp.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      {approver.approverType === 'role' && (
                        <div className="space-y-1">
                          <Label className="text-xs">Role</Label>
                          <Input
                            className="h-9"
                            value={approver.approverRole || ''}
                            onChange={(e) =>
                              updateApprover(stageIndex, approverIndex, {
                                approverRole: e.target.value,
                              })
                            }
                            placeholder="e.g., Manager"
                          />
                        </div>
                      )}
                    </div>

                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeApprover(stageIndex, approverIndex)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}

                {stage.approvers.length === 0 && (
                  <div className="text-center py-4 text-sm text-muted-foreground border rounded-lg border-dashed">
                    No approvers added. Click "Add Approver" to add one.
                  </div>
                )}
              </div>

              <Separator />

              {/* Automation Rules Section */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm font-medium">Automation Rules</Label>
                    <p className="text-xs text-muted-foreground mt-1">
                      Automatically process timesheets based on conditions
                    </p>
                  </div>
                  <Switch
                    checked={stage.enableAutomation || false}
                    onCheckedChange={(checked) => updateStage(stageIndex, { enableAutomation: checked })}
                  />
                </div>

                {stage.enableAutomation && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEditingRulesForStage(stageIndex)}
                    className="w-full"
                  >
                    <Sparkles className="h-4 w-4 mr-2" />
                    Configure Rules ({stage.automationRules?.length || 0})
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}

        {stages.length === 0 && (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <div className="text-center">
                <h3 className="text-lg font-semibold mb-2">No stages yet</h3>
                <p className="text-muted-foreground mb-4">
                  Add at least one approval stage to continue
                </p>
                <Button onClick={addStage}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Stage
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Automation Rule Builder Dialog */}
      {editingRulesForStage !== null && (
        <Dialog open={editingRulesForStage !== null} onOpenChange={() => setEditingRulesForStage(null)}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <AutomationRuleBuilder
              stageId={stages[editingRulesForStage]?.id || `temp-stage-${editingRulesForStage}`}
              stageName={stages[editingRulesForStage]?.name || ''}
              rules={stages[editingRulesForStage]?.automationRules || []}
              onSave={(rules) => {
                updateStage(editingRulesForStage, { automationRules: rules });
                setEditingRulesForStage(null);
              }}
              onCancel={() => setEditingRulesForStage(null)}
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
